import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * PLAY A VIDEO THE CDN WILL ONLY SERVE WHOLE.
 *
 * A `<video src>` streams by asking for byte RANGES. Our hosting compresses the mp4 and then answers those
 * range requests over the COMPRESSED representation — the offsets and the total length describe the brotli
 * stream, not the film — so the decoder is handed bytes that are not an mp4 and reports a fatal media error.
 * The same file fetched WHOLE comes back correct, because a plain 200 declares `Content-Encoding: br` and the
 * browser decodes it transparently before we ever see it. Measured on production 2026-09-13: the range body
 * began `cb ff ff 09`, the whole-file body began `ftyp` and was byte-identical to the file we deployed.
 *
 * So this is the recovery, not the normal path. Streaming is tried first because it starts playing in a
 * fraction of a second and costs nothing extra; only once the element has reported a REAL decode failure do we
 * pay for the whole file and hand the element a blob it cannot get wrong. The moment the hosting header is
 * fixed the fast path simply stops failing and this never runs.
 *
 * ONE ATTEMPT, EVER. A second try would fail identically, and re-assigning `src` re-fires `error` — that is an
 * infinite download loop on the one page we most need to be cheap.
 */
export interface WholeFileMedia {
  /** A blob URL for the fully-downloaded file, once a recovery has succeeded. */
  blobSrc: string | null;
  /** Download the whole file and expose it as a blob. Resolves false when recovery is impossible. */
  recover: () => Promise<boolean>;
}

export function useWholeFileMedia(src: string): WholeFileMedia {
  const [blobSrc, setBlobSrc] = useState<string | null>(null);
  const attempted = useRef(false);
  /** Held in a ref so the revoke on unmount does not depend on the last render's state. */
  const objectUrl = useRef<string | null>(null);

  const release = () => {
    if (objectUrl.current) {
      URL.revokeObjectURL(objectUrl.current);
      objectUrl.current = null;
    }
  };

  // A different film is a different question: let it try the fast path too.
  useEffect(() => {
    attempted.current = false;
    setBlobSrc(null);
    return release;
  }, [src]);

  const recover = useCallback(async (): Promise<boolean> => {
    if (attempted.current) return false;
    attempted.current = true;
    try {
      const res = await fetch(src);
      if (!res.ok) return false;
      const blob = await res.blob();
      // An empty body would swap a broken video for a broken video, so keep the poster instead.
      if (!blob.size) return false;
      release();
      const url = URL.createObjectURL(blob);
      objectUrl.current = url;
      setBlobSrc(url);
      return true;
    } catch {
      // Offline, blocked, or no `fetch` at all — the caller falls back to the poster.
      return false;
    }
  }, [src]);

  return { blobSrc, recover };
}

export default useWholeFileMedia;
